import { useMutation } from '@apollo/client';
import { Button, TextArea, TextInput } from '@ibinder/common-frontend';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import { IMPERSONATE } from '../../../../../../api/queries/queries_local';

// Rendered in a portal on top of any admin modal that opens it (those use
// z-index up to 500), so the impersonate dialog is never covered by the
// content behind it.
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const Container = styled.div`
  width: 450px;
  max-width: calc(100% - 2rem);
  display: flex;
  flex-direction: column;
  min-height: 350px;
  max-height: calc(100% - 2rem);
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.accent};
  box-shadow: ${({ theme }) => theme.shadows.primaryWithOpacity};
  padding: 1rem;

  h2 {
    overflow-wrap: anywhere;
  }

  button {
    margin-top: auto;
  }
`;

const FieldContainer = styled.div`
  margin-bottom: 1rem;
`;

const ErrorText = styled.div`
  color: red;
  margin-bottom: 1rem;
`;

type Props = {
  targetUserId: string;
  userName: string;
  onClose: () => void;
  onImpersonated: (token: string) => void;
};

const ImpersonateUserModal = ({
  targetUserId,
  userName,
  onClose,
  onImpersonated
}: Props) => {
  const modalNode = useRef<HTMLDivElement>();
  const [reason, setReason] = useState('');
  const [supportCase, setSupportCase] = useState('');
  const [showRequiredError, setShowRequiredError] = useState(false);

  const [runImpersonate] = useMutation(IMPERSONATE, {
    onCompleted: (data) => onImpersonated(data.post.impersonationToken)
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!modalNode.current?.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const getInputValue = (value) => value?.target?.value ?? value;

  const handleClick = () => {
    if (!reason.trim()) {
      setShowRequiredError(true);
      return;
    }

    runImpersonate({
      variables: {
        input: {
          targetUserId,
          reason: reason.trim(),
          supportCase: supportCase.trim() || undefined
        }
      }
    });
  };

  return ReactDOM.createPortal(
    <Overlay>
      <Container ref={modalNode}>
        <h2>{`Impersonate user ${userName}?`}</h2>
        <FieldContainer>
          <TextArea
            label="Reason"
            value={reason}
            changeValue={(value) => {
              setReason(getInputValue(value));
              setShowRequiredError(false);
            }}
            required={true}
          />
        </FieldContainer>
        <FieldContainer>
          <TextInput
            label="Support case"
            value={supportCase}
            changeValue={(value) => setSupportCase(getInputValue(value))}
          />
        </FieldContainer>
        {showRequiredError && <ErrorText>Reason is required</ErrorText>}
        <Button btnStyle="primary" label="Continue?" onClick={handleClick} />
      </Container>
    </Overlay>,
    document.body
  );
};

export default ImpersonateUserModal;
